import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { TIERS } from "@/data/slate";

const ThankYou = () => {
    const location = useLocation();
    const { name, tier, message } = location.state || {};
    const tierObj = TIERS.find((t) => t.id === tier);
    const firstName = name ? name.split(" ")[0] : "there";

    return (
        <div className="min-h-screen bg-[#FAF9F6] text-foreground">
            <Header />

            <main className="border-b border-border">
                <div className="mx-auto max-w-4xl px-6 py-24 md:px-10 md:py-32">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-muted-foreground">
                        <CheckCircle2 size={16} strokeWidth={1.5} />
                        Brief received
                    </div>

                    <h1
                        data-testid="thank-you-headline"
                        className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tighter md:text-7xl"
                    >
                        Thank you, {firstName}.
                        <br />
                        <span className="italic">We&apos;ve got it from here.</span>
                    </h1>

                    <p
                        data-testid="thank-you-message"
                        className="mt-10 max-w-2xl text-lg text-muted-foreground"
                    >
                        {message ||
                            "Your Slate brief is now in queue. You'll receive a personal email within one business day with a secure payment link and a short kickoff questionnaire."}
                    </p>

                    {tierObj && (
                        <div
                            data-testid="thank-you-tier-summary"
                            className="mt-12 grid gap-6 border-y border-border py-10 md:grid-cols-3"
                        >
                            <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                                    Tier selected
                                </p>
                                <p className="mt-3 font-serif text-2xl tracking-tight">{tierObj.name}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                                    Investment
                                </p>
                                <p className="mt-3 font-serif text-2xl tracking-tight">
                                    {tierObj.priceLabel}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                                    Typical delivery
                                </p>
                                <p className="mt-3 font-serif text-2xl tracking-tight italic">
                                    48 hours
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="mt-16">
                        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                            What happens next
                        </p>
                        <ol className="mt-6 space-y-6 font-serif text-xl">
                            {[
                                "We review your intake and the job description, line by line.",
                                "You receive a personal email with a secure payment link and a short kickoff questionnaire.",
                                "Once payment is confirmed, your Slate kit lands in your inbox — typically within 48 hours.",
                                "One revision round included. We're here when you need it.",
                            ].map((step, i) => (
                                <li key={step} className="flex items-start gap-5 border-b border-border pb-6">
                                    <span className="font-serif text-xl italic text-muted-foreground">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-foreground">{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className="mt-16 flex flex-wrap items-center gap-5">
                        <Link
                            to="/"
                            data-testid="thank-you-home"
                            className="inline-flex items-center gap-3 rounded-sm border border-primary bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-foreground"
                        >
                            Back to Slate
                            <ArrowRight size={16} strokeWidth={1.5} />
                        </Link>
                        <a
                            href="mailto:support@get-slate.ai"
                            data-testid="thank-you-email"
                            className="slate-link inline-flex items-center gap-2 text-sm text-foreground"
                        >
                            Or email support@get-slate.ai
                        </a>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ThankYou;
